import React, { useMemo, useState, useEffect, useRef } from 'react';
import maxBy from 'lodash/maxBy';

import { Card } from '../Card/Card';
import { OrderList } from './OrderList';
import { SortableOrderList } from './SortableOrderList';
import cx from 'classnames';
import { Map } from '../Map/Map';
import { Button } from '../Button/Button';
import { generatePoints, RouteElementType, getLatLng } from './../../utils/utils';
import { useMapApi } from './../../hooks/useMapApi';
import { MapStatsOverlay } from './MapStatsOverlay';
import { OrderSearch } from './OrderSearch';
import { DriverBreakModal } from './DriverBreakModal';
import { getMapOrderStats } from '../../utils/mapOrderStats';

export const RouteMap = ({
  endPoint,
  startPoint,
  departureTime,
  routeCar,
  orders,
  routeOrders,
  formRouteOrders,
  distance,
  confirmedVolumeM3,
  totalVolumeM3,
  confirmedIncomePrice,
  totalIncomePrice,
  isNotPricedPackages,
  orderConfirmedIncomePrice,
  orderTotalIncomePrice,
  exchangeConfirmedIncomePrice,
  exchangeTotalIncomePrice,
  disableButtons,
  maxRouteOrders,
  routeId,
  handleBeforeApiCall = () => {},
  onRouteCalculated = () => { },
  onSelectOrder = () => { },
  onUnselectOrder = () => { },
  onAddDriverBreakTime,
  onRemoveDriverBreak,
  onEndPointViaChange = () => { },
  onUpdateRouteOrder = () => { },
  onUpdateRouteOrders = () => { },
  hasReturnPicking = false,
}) => {
  const [renderMap, setRenderMap] = useState(true);
  const [mapOnly, setMapOnly] = useState(true);
  const [showAllMarkers, setShowAllMarkers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isDriverBreakModalOpen, setIsDriverBreakModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(() => Boolean(window.google?.maps));

  // Auto-fit is only useful when the planner lands on a route that already
  // has stops (edit/show). On /admin/routes/create the first polyline
  // appears only after the user clicks 'Pokaż trasę' / 'Optymalizuj wg
  // Google', so a 'fit on first polyline' would zoom in right after the
  // user's optimize click — exactly what we want to avoid.
  const mountedWithRouteOrdersRef = useRef(
    (routeOrders ?? []).some((e) => e.type === RouteElementType.ROUTE_ORDER)
  );

  const filteredOrders = useMemo(() => {
    let result = orders;

    if (searchQuery?.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((order) => {
        const postcode = order.deliveryPostcode?.toLowerCase() || '';
        return postcode.includes(query);
      });
    }

    return result;
  }, [orders, searchQuery]);

  const points = useMemo(() => generatePoints(filteredOrders), [filteredOrders]);
  const mapOrderStats = useMemo(() => getMapOrderStats(filteredOrders), [filteredOrders]);

  const routingEngine = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('type') === 'osrm') return 'osrm';
    return 'google';
  }, []);

  useEffect(() => {
    if (window.google?.maps) {
      setIsGoogleMapsReady(true);
      return;
    }

    const handleReady = () => setIsGoogleMapsReady(true);
    window.addEventListener('google-maps-ready', handleReady);
    return () => window.removeEventListener('google-maps-ready', handleReady);
  }, []);

  const { renderStagesOnMap, startPointLocation, endPointLocation, routePolylines, snappedPositions, roadSnappedPositions, routeDistance, routeDuration } = useMapApi({
    routingEngine,
    routeOrders,
    startPoint,
    departureTime,
    endPoint,
    editable: false,
    // In edit mode the planner must always see every point that belongs to
    // the route, including orders already SENT/RECEIVED/CANCELLED. Without
    // this flag, opening a "ready" route for editing would filter out all
    // (already-shipped) waypoints, leaving the map empty and no polyline
    // drawn — the bug fixed for ticket 869d68nm0.
    // ShowRouteContainer passes the same value for the preview view.
    hideInactiveRouteOrders: false,
    onUpdateRouteOrder,
    onUpdateRouteOrders,
    handleBeforeApiCall,
    onStart: () => setIsLoading(true),
    onFinish: () => setIsLoading(false),
  });

  const handleCalculateRoute = async (optimizeWaypoints = false) => {
    const routeResult = await renderStagesOnMap(optimizeWaypoints);

    if (routeResult) {
      onRouteCalculated(routeResult);
    }

    return routeResult;
  };

  const handleOptimize = async () => handleCalculateRoute(true);

  useEffect(() => {
    const via = endPointLocation?.via;

    if (via) {
      onEndPointViaChange(via);
    }
  }, [endPointLocation]);

  useEffect(() => {
    if (!renderMap) return;
    if (!isMapReady) return;
    if (routingEngine === 'google' && !isGoogleMapsReady) return;

    const hasRouteOrders = routeOrders && routeOrders.filter(
      (e) => e.type === RouteElementType.ROUTE_ORDER
    ).length > 0;

    if (hasRouteOrders) {
      handleCalculateRoute(false);
      setRenderMap(false);
      return;
    }

    onRouteCalculated({
      data: [],
      polylines: [],
      snappedPositions: {},
      roadSnappedPositions: {},
      routeDistance: 0,
      routeDuration: 0,
    });
    setRenderMap(false);
  }, [routeOrders, routingEngine, isGoogleMapsReady, isMapReady]);

  const handleLeafletMapChange = () => {};

  const handleSearchChange = (query) => { setSearchQuery(query) };

  const handleClearSearch = () => { setSearchQuery('') };

  const handleMapOnlyToggle = () => setMapOnly((prev) => !prev);

  const leafletMapRef = useRef(null);

  const handleOrderFocus = (order) => {
    if (leafletMapRef.current) {
      const { lat, lng } = getLatLng(order);
      leafletMapRef.current.panTo([lat, lng]);
    }
  }

  const mapCx = cx({
    'col-12': mapOnly,
    'col-6': !mapOnly,
  });

  const renderTitle = () => {
    return <>{__('routes.map')}</>;
  };
  const handelDriverBreakModalOpen = () => setIsDriverBreakModalOpen(true);
  const handleDriverBreakModalClose = () => setIsDriverBreakModalOpen(false);

  const noOrderInConfirmationState = routeOrders
    .filter((element) => element.type === RouteElementType.ROUTE_ORDER)
    .every((routeOrder) => {
      const deliveryConfirmations = (routeOrder.order.deliveryConfirmations ?? [])
        .filter((dc) => dc.routeId === routeId);

      const latest = maxBy(deliveryConfirmations, 'id');

      if (!latest) return true;

      return !['confirmed', 'pending'].includes(latest.status);
    });

  return (
    <div className="row">
      {!mapOnly && (
        <>
          <div className="col-3">
            <Card title={__('routes.orders')} className='h-750px'>
              {isLoading && <div className="map__loader map__loader--transparent" />}
              <OrderSearch
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
              />

              {filteredOrders?.length === 0 && (
                <div className="mb-2">
                  <small className="text-muted">
                    {__('app.no_result')}
                  </small>
                </div>
              )}
              <div className="route-orders" id="orders-list-for-ship">
                <OrderList
                  orders={filteredOrders}
                  onOrderFocus={handleOrderFocus}
                  routeId={routeId}
                />
              </div>
            </Card>
          </div>
          <div className="col-3 position-relative">
            <Card title={__('routes.added')} className='h-750px'>
              {isLoading && <div className="map__loader map__loader--transparent" />}

              {routeOrders?.length > 0 && (
                <Button onClick={handelDriverBreakModalOpen} className="btn-sm btn-primary mr-5px">
                  {__('routes.add_driver_break')}
                </Button>
              )}

              <DriverBreakModal
                routeOrders={routeOrders}
                isOpen={isDriverBreakModalOpen}
                onModalClose={handleDriverBreakModalClose}
                onAddDriverBreakTime={onAddDriverBreakTime}
              />

              <div className="route-orders route-orders--selected">
                <SortableOrderList
                  routeOrders={routeOrders}
                  formRouteOrders={formRouteOrders}
                  onClick={onUnselectOrder}
                  onFocusClick={handleOrderFocus}
                  maxRouteOrders={maxRouteOrders}
                  onDragEnd={onUpdateRouteOrders}
                  onRemoveDriverBreak={onRemoveDriverBreak}
                  routeId={routeId}
                />
              </div>
            </Card>
          </div>
        </>
      )}
      <div className={(mapCx)}>
        <Card title={renderTitle()} className='h-750px'>
          <Map
            orders={orders}
            routeOrders={routeOrders}
            points={points}
            onSelectOrder={onSelectOrder}
            onUnselectOrder={onUnselectOrder}
            showAllMarkers={showAllMarkers}
            routeId={routeId}
            isLoading={isLoading}
            startPointLocation={startPointLocation?.point}
            endPointLocation={endPointLocation?.point}
            onMapLoad={(map) => {
              leafletMapRef.current = map;
              setIsMapReady(true);
            }}
            onGoogleMapChange={handleLeafletMapChange}
            routePolylines={routePolylines}
            snappedPositions={snappedPositions}
            roadSnappedPositions={roadSnappedPositions}
            fitRouteBoundsOnRender={routePolylines.length > 0 && mountedWithRouteOrdersRef.current}
            hasReturnPicking={hasReturnPicking}
          >
            <MapStatsOverlay
              hasReturnPicking={hasReturnPicking}
              confirmedVolumeM3={confirmedVolumeM3}
              totalVolumeM3={totalVolumeM3}
              car={routeCar}
              confirmedIncomePrice={confirmedIncomePrice}
              totalIncomePrice={totalIncomePrice}
              isNotPricedPackages={isNotPricedPackages}
              orderConfirmedIncomePrice={orderConfirmedIncomePrice}
              orderTotalIncomePrice={orderTotalIncomePrice}
              exchangeConfirmedIncomePrice={exchangeConfirmedIncomePrice}
              exchangeTotalIncomePrice={exchangeTotalIncomePrice}
              distance={distance}
              routeDistance={routeDistance}
              routeDuration={routeDuration}
              mapOrderCount={mapOrderStats.orderCount}
              mapOrderVolumeM3={mapOrderStats.volumeM3}
            />
          </Map>
          <div className="row route-map__toolbar">
            <div className="col-4">
              <div className="route-map__toolbar-group">
              {!hasReturnPicking && (
                <>
                  <Button
                    className="btn-secondary waves-effect waves-light"
                    tooltip="Powiększ mapę"
                    onClick={handleMapOnlyToggle}
                    icon="bx bx-area"
                  />
                  <Button
                    className="btn-primary waves-effect waves-light"
                    tooltip={__('routes.all_orders')}
                    onClick={() => setShowAllMarkers(true)}
                    icon="bx bx-map-pin"
                  />
                </>
              )}
              <button
                type="button"
                disabled={routeOrders.length === 0}
                className="btn btn-icon btn-primary waves-effect waves-light"
                data-toggle="tooltip"
                data-original-title={__('routes.all_added')}
                onClick={() => setShowAllMarkers(false)}
              >
                <i className="bx bx-map-alt" />
              </button>
              </div>
            </div>
            <div className="col-8">
              <div className="route-map__toolbar-actions">
              {disableButtons && (
                <span className="text-danger small mr-2">
                  <i className="bx bx-error-circle" /> {__('routes.validation.speed_required')}
                </span>
              )}
              {noOrderInConfirmationState && (
                <Button
                className="btn-primary"
                disabled={isLoading || disableButtons || routeOrders.length === 0}
                onClick={handleOptimize}
                >
                  {__('routes.google_optimize')}
                </Button>
              )}
              <Button
                className="btn-success bck-kn-2 btn-primary-rightfix"
                disabled={isLoading || disableButtons || routeOrders.length === 0}
                onClick={() => handleCalculateRoute(false)}
              >
                {__('routes.show_route')}
              </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
